<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import Steps from "@/components/agent-feedback/Steps.vue";
import StepsBullet from '@/components/agent-feedback/StepsBullet.vue';
import { useNotificationStore, useClaimStore } from '@/store';

import FormEligibilityVerification from '@/components/agent-feedback/FormEligibilityVerification.vue';
import FormAdditionalDamages from '@/components/agent-feedback/FormAdditionalLoss.vue';
import FormUploadDocuments from '@/components/agent-feedback/FormUploadDocuments.vue';
import Alert from "@/components/ui/alerts/Alert.vue";
import { ArrowTopRightOnSquareIcon, InformationCircleIcon } from "@heroicons/vue/24/solid";
import AlertModal from '@/components/ui/modals/AlertModal.vue';
import Overview from '@/pages/plugin/Overview.vue';
import PassengerTable from '@/components/passengers/PassengerTable.vue';
import BaseButton from '@/components/ui/buttons/BaseButton.vue';
import CancelledClaimBanner from "@/components/ui/banners/CancelledClaimBanner.vue";

import { scrollToTop } from '@/utils';
import { RetrieveClaimWithRelationsResponse } from '@/interfaces/data-contracts';
import ClaimStatePreview from '@/components/claim/ClaimStatePreview.vue';
import { ArrowsPointingOutIcon } from '@heroicons/vue/24/outline';
import { ZoomInIcon } from 'lucide-vue-next';
import TemporaryAccountManagerBanner from "@/components/ui/banners/TemporaryAccountManagerrBanner.vue";

export interface ClaimOverview {
  claimId: string;
  feedbackId?: string;
  pnrId: string;
  flightIdentifier: string;
  ec261CompensationAmount: number;
  travelAgencyCompany: any;
  claimType: string;
  travelAgencyCompanyName?: string | null;
  stateUpdatedAt?: Date;
  isOfficeB2b: boolean;
  form: RetrieveClaimWithRelationsResponse["agentFeedback"];
  passengers: {
    id: string;
    firstName: string;
    lastName: string;
  }[];
}
// const claimOverview = computed<ClaimOverview>(() => useRoute().query as any)
const claim = computed(() => useClaimStore().claim);
const claimOverview = ref<ClaimOverview>(getClaimOverview());
function getClaimOverview() : ClaimOverview {
  const claim = useClaimStore().claim;
  return {
    claimId: claim.id,
    feedbackId: claim.agentFeedback?.id,
    pnrId: claim.legalSpecification.impactedSegment.reservation.gdsPnrId,
    flightIdentifier: claim.legalSpecification.effectiveFlight.flightIdentifier,
    ec261CompensationAmount: claim.legalSpecification.ec261CompensationAmount,
    travelAgencyCompany: claim.legalSpecification.impactedSegment.reservation.travelAgencyCompany,
    claimType: claim.claimType.name,
    travelAgencyCompanyName: claim.legalSpecification.impactedSegment.reservation.travelAgencyCompany?.displayName,
    stateUpdatedAt: claim.stateUpdatedAt ? new Date(claim.stateUpdatedAt) : undefined,
    isOfficeB2b: Boolean(claim.legalSpecification.impactedSegment.reservation.travelAgencyOffice.isBusinessTravel),
    passengers: claim.usersClaims.map(({ user }) => ({
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName
    })),
    form: claim.agentFeedback,
  }
}

const steps = [
  {
    stepNumber: 0,
    name: "Confirmation",
    description: "Valider le dossier",
    form: FormEligibilityVerification,
    alert: {
      title: `Feedback demandé (${claimOverview.value.pnrId})`,
      message: `Nous avons besoin de votre confirmation sur ce dossier`,
    }
  },
  {
    stepNumber: 1,
    name: "Préjudices",
    description: "Établir les demandes supplémentaires",
    form: FormAdditionalDamages,
  },
  {
    stepNumber: 2,
    name: "Signatures",
    description: "Attente signature cessions de créances",
    form: FormUploadDocuments,
  },
]
function getStep() : number {
  const agentFeedback = claim.value.agentFeedback;
  const signatureStates = [
    "CLAIM_VALIDATION.WAIT_FOR_RECEIVABLE_CONTRACT_SIGNATURES",
    "CLAIM_VALIDATION.WAIT_FOR_RECEIVABLE_CONTRACT_SIGNATURES_B2C_AND_B2B",
    "CLAIM_VALIDATION.WAIT_FOR_ENTREPRISE_SIGNATURE",
  ];
  
  if (agentFeedback?.additionalLoss || signatureStates.includes(claim.value.claimState.type)) {
    return 2; // Download / Generate receivable document
  } else if (agentFeedback?.validationOptionId) return 1

  else if (claim.value.claimState.type === "CLAIM_VALIDATION.WAIT_FOR_AGENT_FEEDBACK") {
    return 0; // Agent confirmation
  }

  return 1; // AdditionalLoss form
}
const currentStep = ref(getStep());

async function nextStep(number = 1) {
  scrollToTop();
  currentStep.value += number;
  await useClaimStore().retrieveClaim(useClaimStore().claim.id);
  claimOverview.value = getClaimOverview();
  useNotificationStore().emitNotification({
    title: "Éléments enregistrés avec succès",
    type: "success",
  }, 5000)
}
const reservationOpen = ref(false);
const showBanner = ref(true);
</script>
<template>
<CancelledClaimBanner v-if="claim.claimLegalStep.name === 'CANCELLED'" v-show="showBanner" @close="showBanner = false"/>
<div v-else-if="claim.claimState.legalStepId !== 0">
  <div
    class="py-16 pointer-events-none bg-stone-50 sm:flex sm:justify-center sm:px-6 lg:px-8"
  >
    <div class="pointer-events-auto shadow-xl flex items-center border-2 border-stone-600 justify-between gap-x-6 bg-stone-700 cursor-help px-6 py-2.5 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-3.5"
    >
      <p class="text-sm leading-6 text-white">
        <span class="inline-flex items-center">
          <InformationCircleIcon class="w-5 h-5 mr-3" />
          <strong class="font-semibold">Sur ce dossier : Flux de contact manuel (via l'agence)</strong>
          <!-- <svg viewBox="0 0 2 2" class="mx-2 inline h-0.5 w-0.5 fill-current" aria-hidden="true">
            <circle cx="1" cy="1" r="1" />
          </svg> -->
          <!-- <span>Upclaim envoie la demande de signature automatiquement</span> -->
          <!-- &nbsp;<span aria-hidden="true">&rarr;</span> -->
        </span>
      </p>
    </div>
  </div>
</div>
<div v-else>

  <!-- <TemporaryAccountManagerBanner
    v-if="claimOverview.travelAgencyCompany"
    v-permission="['UPCLAIM_ADMIN', 'CUSTOMER_ACCOUNT_MANAGER']"
    :isDirectPassengerContactAllowed="claimOverview.travelAgencyCompany?.isDirectPassengerContactAllowed"
    :claimCreatedAt="new Date(claim.createdAt)"
  /> -->
  <AlertModal type="info"
    title="Visualiser la demande"
    message="La réservation et le segment détecté par Upclaim"
    confirm-message="" 
    :open=reservationOpen @close="reservationOpen = false" @open="reservationOpen = true"
    width="sm:max-w-7xl max-w-7xl" 
    @confirm="() => {
      reservationOpen = false
    }">
  
    <div class="flex w-full divide-y-2">
      <div class="w-1/2 px-6">
        <Overview />
      </div>
      <div class="w-1/2">
        <PassengerTable />
      </div>
    </div>

  </AlertModal>
  <div v-if="steps[currentStep].alert">
    <Alert 
      :title="(steps[currentStep] as any).alert.title"
      :titleNote="claimOverview.stateUpdatedAt
        ? `Il y a ${ formatDistanceToNow(claimOverview.stateUpdatedAt, { locale: fr }) }`
        : undefined"
      :type="((steps[currentStep] as any).alert.type)"
      :message="(steps[currentStep] as any).alert.message"
    />
  </div>
  <div id="teleport-agent-feedback-alert"></div>
  <!-- <h2 class="py-4 text-lg font-semibold leading-8">Feedback Agent</h2> -->
  <div class="px-4 pt-2 pb-6 text-left bg-gray-50">
    <div class="flex justify-center py-2" v-permission="['UPCLAIM_ADMIN']">
      <span class="inline-flex rounded-md shadow-sm isolate">
        <button type="button" @click="currentStep ? currentStep-- : null" class="relative inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white rounded-l-md ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
          Précédent
        </button>
        <button type="button" @click="currentStep < steps.length - 1 ? currentStep++ : null" class="relative inline-flex items-center px-3 py-2 -ml-px text-sm font-semibold text-gray-900 bg-white rounded-r-md ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
          Suivant
        </button>
        <a v-if="claimOverview.feedbackId" :href="`https://app.forestadmin.com/Upclaim/Production/Upclaim/data/claimsAgentsFeedbacks/index/record/claimsAgentsFeedbacks/${claimOverview.feedbackId}/details`" target="_blank" class="relative inline-flex items-center px-3 py-2 ml-4 text-sm font-semibold text-gray-900 bg-white rounded-md ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
          <ArrowTopRightOnSquareIcon class="w-5 h-5 text-gray-500 -m-0.5 mr-1" />
          Ouvrir
        </a>
      </span> 
    </div>


    <!-- Steps -->
    <div class="py-5 text-gray-700 bg-white border rounded-lg shadow">
      <StepsBullet :steps="steps" :current-step="currentStep" class="" />
    </div>

    <div class="flex content-center w-full py-8 border-b">
      <div class="mx-auto">
        <BaseButton size="lg" text="Voir le dossier complet" @click="reservationOpen = true" :icon="ZoomInIcon" />
      </div>
    </div>

    <!-- Form -->
    <div class="px-6 py-8 text-left bg-white border rounded-md shadow">
      <component :is="steps[currentStep].form" :claim-overview="claimOverview" @nextStep="nextStep" />
    </div>
  </div>
</div>
</template>